/**
 * The one place a server timestamp becomes text a human reads.
 *
 * TWO independent defects live here. Both were reported on beta day one
 * (2026-08-01) and neither is fixed by the other.
 *
 * ── 1. A zone-less timestamp parses as LOCAL ─────────────────────────────────
 * PostgREST serializes a `timestamp WITHOUT time zone` column with no offset —
 * "2026-08-01T00:27:41.901207" — and per ES2015+ `new Date()` reads a zone-less
 * ISO date-TIME as local. A UTC instant therefore renders +4h in EDT / +5h in
 * EST. Reproduced in Nick's own Chrome: a bug filed at 8:27:41 PM ET on Jul 31
 * printed as "8/1/2026, 12:27:41 AM" — tomorrow's date, near midnight.
 *
 * `supabase/migrations/20260801060000` converts the three ops intake tables to
 * `timestamptz`, which fixes it at the source. `parseServerDate` stays anyway,
 * for two reasons that outlive that migration:
 *   · the migration applies on MERGE and this bundle ships on the REPUBLISH —
 *     two separate events, with a window between them;
 *   · ~100 naive timestamp columns remain elsewhere in the schema, so any
 *     future supabase-js read of one lands here already correct.
 *
 * ── 2. Nothing pinned a zone ────────────────────────────────────────────────
 * Bare `toLocaleString()` renders in the VIEWER's zone with no label, so two
 * admins reading one SLA clock see two different times and neither can tell
 * which is which. Nick's ruling (2026-08-01): **UTC storage, ET display.**
 * Instants and clock math stay UTC; every human-facing surface renders
 * America/New_York.
 *
 * ── Where the label goes, and why not everywhere ────────────────────────────
 * `formatDateTime` prints the zone ("EDT"/"EST") because a time is the thing a
 * zone changes and the thing two people compare. `formatDate` does not: it is
 * pinned to ET so the date is stable, but "Aug 1, 2026 EDT" is noise on a
 * surface that shows no time. If that call turns out wrong it is one line in
 * this file, which is the point of having one file.
 */

export const DISPLAY_TIME_ZONE = "America/New_York";

/** What every formatter here returns when there is nothing to render. */
export const EMPTY_DATE = "—";

/**
 * Coerce a server timestamp into a Date, treating a zone-less string as UTC.
 *
 * A zone-less ISO date-TIME ("2026-08-01T00:27:41.901207", or the Postgres
 * space-separated form) is stamped UTC before parsing. Anything that already
 * carries an offset — "…Z", "…+00:00", "…-04:00" — is passed through untouched,
 * so a correctly-typed `timestamptz` column is never double-corrected.
 *
 * A DATE-only string ("2026-08-01") is left alone: ES2015+ already parses that
 * as UTC, and stamping it would not change the result.
 *
 * Returns null for null/undefined/empty/unparseable input so callers render the
 * empty marker instead of "Invalid Date".
 */
export function parseServerDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value === "number") {
    const fromEpoch = new Date(value);
    return Number.isNaN(fromEpoch.getTime()) ? null : fromEpoch;
  }

  const trimmed = value.trim();
  if (!trimmed) return null;

  // Date-and-time, no offset, no trailing zone designator ⇒ stamp it UTC.
  // Accepts both the ISO "T" separator and Postgres' space separator.
  const zoneless = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
  const normalized = zoneless.test(trimmed) ? `${trimmed.replace(" ", "T")}Z` : trimmed;

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * "Jul 31, 2026, 8:27 PM EDT" — pinned to Eastern, zone printed.
 * Use wherever the reader needs the time of day.
 */
export function formatDateTime(value: string | number | Date | null | undefined): string {
  const date = parseServerDate(value);
  if (!date) return EMPTY_DATE;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: DISPLAY_TIME_ZONE,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(date);
}

/**
 * Today's ET calendar date as `YYYY-MM-DD`, for date-range pickers.
 *
 * Replaces `new Date().toISOString().slice(0, 10)`, which returns **tomorrow**
 * after 8 PM EDT / 7 PM EST — so every admin default range silently shifted a
 * day for anyone working evenings, which is most evenings here.
 *
 * ⚠️ Only safe because the SERVER buckets moved to Eastern in the same change
 * (api-server `lib/reportingWindow.ts`). On its own this would be a REGRESSION:
 * the server used to read a `to` date as a UTC day, so handing it the ET date
 * would have cut the window at 8 PM ET and dropped the evening's activity.
 */
export function todayET(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: DISPLAY_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** `todayET()` shifted by `days` (negative for the past), still an ET date. */
export function etDateOffset(days: number, now: Date = new Date()): string {
  return todayET(new Date(now.getTime() + days * 86_400_000));
}

/**
 * Renders a UTC scheduling input as its Eastern equivalent, with the offset.
 *
 *   "2026-08-05T14:00"  →  "Aug 5, 2026, 10:00 AM EDT (UTC−4)"
 *
 * For the email-blast scheduler, where the field is UTC (matching how the
 * server stores and fires every scheduled job) and the admin thinks in ET.
 *
 * The offset is COMPUTED for that instant, never hardcoded. EST is UTC−5 and
 * EDT is UTC−4, so a fixed "(UTC−4)" label is wrong from early November to
 * mid-March — and wrong in the direction that makes an 8am send go out at 7am.
 * Intl resolves the right one either side of the DST boundary.
 *
 * The input is a `datetime-local` value, which has no offset. parseServerDate
 * stamps zone-less date-times as UTC, which is exactly the reading we want and
 * exactly the reading `new Date(value)` does NOT give — that parses in the
 * browser's zone, which is the live defect in the Campaigns tab's own
 * "(UTC)"-labelled field.
 */
export function formatUtcInputAsEastern(value: string | null | undefined): string {
  const date = parseServerDate(value);
  if (!date) return EMPTY_DATE;

  const stamp = formatDateTime(date);
  const rawOffset = new Intl.DateTimeFormat("en-US", {
    timeZone: DISPLAY_TIME_ZONE,
    timeZoneName: "longOffset",
  })
    .formatToParts(date)
    .find((p) => p.type === "timeZoneName")?.value ?? "";

  // "GMT-04:00" → "UTC−4"; "GMT-04:30" → "UTC−4:30"
  const parts = /GMT([+-])(\d{2}):(\d{2})/.exec(rawOffset);
  if (!parts) return stamp;
  const sign = parts[1] === "-" ? "−" : "+";
  const minutes = parts[3] === "00" ? "" : `:${parts[3]}`;
  return `${stamp} (UTC${sign}${Number(parts[2])}${minutes})`;
}

/**
 * "Jul 31" — the same Eastern pin as formatDate, without the year.
 *
 * Exists for Blake's demo wallet, whose escrow/payout rows are laid out for a
 * short date ("Aug 20 · with @miacurates") and wrap badly with a year in them.
 * Deliberately NOT a substring of formatDate's output: the year is not always
 * last in every locale, and slicing it off would be a formatting bug waiting
 * for a locale change rather than a formatter.
 */
export function formatMonthDay(value: string | number | Date | null | undefined): string {
  const date = parseServerDate(value);
  if (!date) return EMPTY_DATE;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: DISPLAY_TIME_ZONE,
    month: "short",
    day: "numeric",
  }).format(date);
}

/**
 * "4:32 PM" — the clock half of the pair above, same Eastern pin.
 *
 * Blake's deal threads carry the date and the time as SEPARATE fields
 * (offerWhen / offerTime), so they are two formatters rather than one string
 * that a caller has to split. Splitting a combined stamp would put a parser in
 * the display path, which is exactly the shape of the bug in his own offer-feed
 * sort (it does parseInt on a space-delimited date and silently yields 0).
 */
export function formatClockTime(value: string | number | Date | null | undefined): string {
  const date = parseServerDate(value);
  if (!date) return EMPTY_DATE;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: DISPLAY_TIME_ZONE,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
}

/**
 * "June 2026" — a join date, at the granularity a join date is read at.
 *
 * FULL month name, not the short one every other formatter here uses, because
 * this one has a consumer with a fixed shape: Blake's demo renders it two ways
 * off the same string — whole ("Member since June 2026", app.js:1158, :1338)
 * and as `memberSince.split(" ").pop()` for the Top Collections card, which
 * takes the LAST space-delimited token as the year (app.js:2041). A two-token
 * "<Month> <Year>" is what makes both readings correct at once; his own fixture
 * rows are written that way ("June 2026", "March 2026"). Adding a day would
 * make that card print a year that is not a year.
 */
export function formatMonthYear(value: string | number | Date | null | undefined): string {
  const date = parseServerDate(value);
  if (!date) return EMPTY_DATE;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: DISPLAY_TIME_ZONE,
    month: "long",
    year: "numeric",
  }).format(date);
}

/**
 * "Jul 31, 2026" — pinned to Eastern so the DATE cannot roll, no zone label.
 * Use wherever only the day matters.
 */
export function formatDate(value: string | number | Date | null | undefined): string {
  const date = parseServerDate(value);
  if (!date) return EMPTY_DATE;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: DISPLAY_TIME_ZONE,
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}
